/**
 * Veřejný changelog — čtecí hooky pro apex stránky.
 *
 * Odpověď serveru **neobsahuje** ``sources`` (čísla PR); hranice mezi
 * interním a veřejným je na serveru, ne tady.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

const BASE = "/api/v1/changelog";

export type PublicChangelogTier = "article" | "patch_notes";

export interface PublicChangelogCard {
  slug: string;
  title: string;
  tier: PublicChangelogTier;
  version_tag: string | null;
  released_at: string | null;
  module_tags: string[];
  hero_asset_key: string | null;
  excerpt: string | null;
}

/**
 * Filtrovací chip. Nese ``label``, protože klíč modulu je interní
 * taxonomie — vyrenderovaný do chipu dělal z české stránky seznam
 * „communication / documents / production" (KAN-719). Server
 * nepojmenovaný modul vůbec nevrací.
 */
export interface PublicChangelogModule {
  key: string;
  label: string;
}

export interface PublicChangelogIndex {
  items: PublicChangelogCard[];
  lang: string;
  /** Moduly, které se skutečně vyskytují — podklad pro filtrovací chipy. */
  modules: PublicChangelogModule[];
}

/**
 * Blok tak, jak dorazí z veřejného API. Volnější než admin typy: pole
 * ``plan_name`` u dostupnosti dopočítává server a renderer musí umět
 * i tvar, který ještě nezná — starý článek přežije přidání bloku.
 */
export interface PublicBlock {
  type: string;
  [key: string]: unknown;
}

export interface PublicChangelogDetail {
  /** Slug článku — identita napříč jazyky, ne adresa (KAN-1971). */
  slug: string;
  /** Adresa v jazyce odpovědi; jiná než v URL = přesměruj na ni. */
  canonical_slug: string;
  /** Adresa v každém jazyce platformy — podklad pro hreflang. */
  slugs: Record<string, string>;
  title: string;
  tier: PublicChangelogTier;
  version_tag: string | null;
  released_at: string | null;
  module_tags: string[];
  hero_asset_key: string | null;
  lang: string;
  blocks: PublicBlock[];
}

export function usePublicChangelog(lang: string, module: string | null) {
  const query = new URLSearchParams({ lang });
  if (module) query.set("module", module);
  return useQuery<PublicChangelogIndex>({
    queryKey: ["public", "changelog", lang, module],
    queryFn: () => apiFetch<PublicChangelogIndex>(`${BASE}?${query}`),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePublicChangelogPost(slug: string | undefined, lang: string) {
  return useQuery<PublicChangelogDetail>({
    queryKey: ["public", "changelog", "post", slug, lang],
    queryFn: () =>
      apiFetch<PublicChangelogDetail>(
        `${BASE}/${slug}?${new URLSearchParams({ lang })}`,
      ),
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
  });
}
